import { Box, IconButton } from '@mui/material';
import {
  GridGroupNode,
  GridRenderCellParams,
  GridTreeNode,
  useGridApiContext,
} from '@mui/x-data-grid-pro';
import { ExpandMore, ChevronRight } from '@mui/icons-material';
import { TransportationAttributeRowItem } from './TransportationAttributesGridContainer';
import { styled } from '@mui/system';

const PREFIX = 'TransportationAttributesGroupCell';

const classes = {
  arrowButton: `${PREFIX}-arrowButton`,
};

interface StyledCellProps {
  depth: number;
}

const GroupCellBox = styled(Box)<StyledCellProps>(({ theme, depth }) => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  paddingLeft: theme.spacing(3 * depth),

  [`& .${classes.arrowButton}`]: {
    padding: '4px',
  },
}));

const SingleCellBox = styled(Box)<StyledCellProps>(({ theme, depth }) => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  paddingLeft: `calc(${theme.spacing(3 * depth)} + ${theme.spacing(4)})`,
}));

export const TransportationAttributesGroupCell = (
  params: GridRenderCellParams<TransportationAttributeRowItem>,
) => {
  const apiRef = useGridApiContext();
  const rowNode: GridTreeNode | null = apiRef.current.getRowNode(params.id);

  // Check if this is a group/parent row
  const isGroupRow =
    rowNode?.type === 'group' || Boolean(params.row.children?.length);
  const depth: number = rowNode?.depth ?? 0;

  const displayName = params.formattedValue?.split('#')[0] ?? '';
  if (!isGroupRow) {
    return (
      <SingleCellBox depth={depth}>
        <Box>{displayName}</Box>
      </SingleCellBox>
    );
  }

  // For group rows, we need to render the default cell structure manually
  // to preserve the expand/collapse functionality
  const isExpanded: boolean =
    (rowNode as GridGroupNode)?.childrenExpanded ?? false;

  // Create the expand/collapse button click handler
  const handleIconClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (isExpanded) {
      apiRef.current.setRowChildrenExpansion(params.id, false);
    } else {
      apiRef.current.setRowChildrenExpansion(params.id, true);
    }
  };

  return (
    <GroupCellBox depth={depth}>
      <IconButton
        size="small"
        onClick={handleIconClick}
        className={classes.arrowButton}>
        {isExpanded ? <ExpandMore /> : <ChevronRight />}
      </IconButton>

      <Box>{displayName}</Box>
    </GroupCellBox>
  );
};
